import { AQL_QUERY_TYPE } from './constants';
import {
  AQLQuery,
  AQLQueryCondition,
  AQLQueryType,
  AQLQueryValue,
  AQLRangeCondition,
  AQLContextualFilterType,
  AQLNamedEntitiesFilterType,
  AQLLinkedNamedEntitiesFilterType,
  AQLPodcastMetadataFilterType,
} from './types';
import { mergeFilters } from './utils';
import {
  CategoryType,
  GarmRiskLevelType,
  IabBrandSafetyCategoryType,
  IabBrandSafetyRiskLevelType,
} from '@insights_module/types/BrandSafety';
import {
  AppleFilterType,
  ContextualTaxonomy,
  IABFilterType,
} from '@insights_module/types/ContextualAndEntity';

// Base class for all AQL queries
export abstract class AQLBaseQuery {
  constructor(
    type:
      | AQLQueryType
      | AQLContextualFilterType
      | AQLNamedEntitiesFilterType
      | AQLPodcastMetadataFilterType,
    condition: keyof AQLQueryCondition,
    value: AQLQueryValue
  ) {
    if (condition === 'use_value') return { [type]: value } as AQLQuery;

    return { [type]: { [condition]: value } } as AQLQuery;
  }
}

// Audio queries
export class AQLTitleQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>, title: string) {
    super(AQL_QUERY_TYPE.TITLE, condition, title);
  }
}

export class AQLDurationQuery extends AQLBaseQuery {
  constructor(condition: 'eq', duration: number);
  constructor(condition: 'range', duration: AQLRangeCondition<number>);
  constructor(condition: 'eq' | 'range', duration: number | AQLRangeCondition<number>) {
    super(AQL_QUERY_TYPE.DURATION, condition, duration);
  }
}

export class AQLOriginalURLQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, url: string) {
    super(AQL_QUERY_TYPE.ORIGINAL_URL, condition, url);
  }
}

export class AQLPublishedDateQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    date: AQLRangeCondition<string>
  );
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, date: string);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range' | 'eq'>,
    date: string | AQLRangeCondition<string>
  ) {
    super(AQL_QUERY_TYPE.PUBLISHED_DATE, condition, date);
  }
}

export class AQLCreatedAtQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    date: AQLRangeCondition<string>
  );
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, date: string);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range' | 'eq'>,
    date: string | AQLRangeCondition<string>
  ) {
    super(AQL_QUERY_TYPE.CREATED_AT, condition, date);
  }
}

export class AQLUpdatedAtQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    date: AQLRangeCondition<string>
  );
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, date: string);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range' | 'eq'>,
    date: string | AQLRangeCondition<string>
  ) {
    super(AQL_QUERY_TYPE.UPDATED_AT, condition, date);
  }
}

export class AQLGroupsQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    groupFilter: AQLQuery
  ) {
    super(AQL_QUERY_TYPE.GROUPS, condition, groupFilter);
  }
}

export class AQLTranscriptQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    transcript: string
  ) {
    super(AQL_QUERY_TYPE.TRANSCRIPT, condition, transcript);
  }
}

export class AQLLanguageQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq' | 'excludes'>, language: string);
  constructor(condition: Extract<keyof AQLQueryCondition, 'any_of'>, language: string[]);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'excludes' | 'any_of'>,
    language: string | string[]
  ) {
    super(AQL_QUERY_TYPE.LANGUAGE, condition, language);
  }
}

// Group queries

export class AQLIdQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, id: string);
  constructor(condition: Extract<keyof AQLQueryCondition, 'any_of'>, id: string[]);
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of'>, id: string | string[]) {
    super(AQL_QUERY_TYPE.ID, condition, id);
  }
}

export class AQLNameQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>, name: string) {
    super(AQL_QUERY_TYPE.NAME, condition, name);
  }
}

export class AQLTypeQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, type: string);
  constructor(condition: Extract<keyof AQLQueryCondition, 'any_of'>, type: string[]);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of'>,
    type: string | string[]
  ) {
    super(AQL_QUERY_TYPE.TYPE, condition, type);
  }
}

export class AQLAudioCountQuery extends AQLBaseQuery {
  constructor(condition: Extract<keyof AQLQueryCondition, 'eq'>, count: number);
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    count: AQLRangeCondition<number>
  );
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    count: number | AQLRangeCondition<number>
  ) {
    super(AQL_QUERY_TYPE.AUDIO_COUNT, condition, count);
  }
}

export class AQLExternalIdFilterQuery extends AQLBaseQuery {
  constructor(type: 'name' | 'value', value: string | number) {
    super(type, 'eq', value);
  }
}

export class AQLExternalIdsQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    externalFilters: AQLExternalIdFilterQuery[]
  ) {
    super(AQL_QUERY_TYPE.EXTERNAL_IDS, condition, mergeFilters(externalFilters));
  }
}

export class AQLExtraFieldsFilterQuery extends AQLBaseQuery {
  constructor(
    type: 'name' | 'value',
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'wildcard'>,
    extraField: string
  ) {
    super(type, condition, extraField);
  }
}

export class AQLExtraFieldsQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    extraFilters: AQLExtraFieldsFilterQuery[]
  ) {
    super(AQL_QUERY_TYPE.EXTRA_FIELDS, condition, mergeFilters(extraFilters));
  }
}

// Shows metadata queries

export class AQLPodcastMetadataFilterQuery extends AQLBaseQuery {
  constructor(
    metadataFilterType: AQLPodcastMetadataFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'includes' | 'excludes'>,
    value: string
  );
  constructor(
    metadataFilterType: AQLPodcastMetadataFilterType,
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    value: AQLRangeCondition<number>
  );
  constructor(
    metadataFilterType: AQLPodcastMetadataFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'includes' | 'excludes'>,
    value: string | AQLRangeCondition<number>
  ) {
    super(metadataFilterType, condition, value);
  }
}

export class AQLPodcastMetadataQuery extends AQLBaseQuery {
  constructor(filters: AQLPodcastMetadataFilterQuery[]) {
    super(AQL_QUERY_TYPE.PODCAST_METADATA, 'use_value', mergeFilters(filters));
  }
}

//NLP queries

export class AQLBrandSafetyGarmFilterQuery extends AQLBaseQuery {
  constructor(
    category: CategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq'>,
    value: GarmRiskLevelType
  );
  constructor(
    category: CategoryType,
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    value: AQLRangeCondition<GarmRiskLevelType>
  );
  constructor(
    category: CategoryType,
    condition: Extract<keyof AQLQueryCondition, 'any_of'>,
    value: GarmRiskLevelType[]
  );
  constructor(
    category: CategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'any_of'>,
    value: GarmRiskLevelType | AQLRangeCondition<GarmRiskLevelType> | GarmRiskLevelType[]
  ) {
    super(category as AQLQueryType, condition, value);
  }
}

export class AQLBrandSafetyGarmQuery extends AQLBaseQuery {
  constructor(filters: AQLBrandSafetyGarmFilterQuery[]) {
    super(AQL_QUERY_TYPE.BRAND_SAFETY_GARM, 'use_value', mergeFilters(filters));
  }
}

export class AQLBrandSafetyIabFilterQuery extends AQLBaseQuery {
  constructor(
    category: IabBrandSafetyCategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq'>,
    value: IabBrandSafetyRiskLevelType
  );
  constructor(
    category: IabBrandSafetyCategoryType,
    condition: Extract<keyof AQLQueryCondition, 'range'>,
    value: AQLRangeCondition<IabBrandSafetyRiskLevelType>
  );
  constructor(
    category: IabBrandSafetyCategoryType,
    condition: Extract<keyof AQLQueryCondition, 'any_of'>,
    value: IabBrandSafetyRiskLevelType[]
  );
  constructor(
    category: IabBrandSafetyCategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'any_of'>,
    value:
      | IabBrandSafetyRiskLevelType
      | AQLRangeCondition<IabBrandSafetyRiskLevelType>
      | IabBrandSafetyRiskLevelType[]
  ) {
    super(category as AQLQueryType, condition, value);
  }
}

export class AQLBrandSafetyIabQuery extends AQLBaseQuery {
  constructor(filters: AQLBrandSafetyIabFilterQuery[]) {
    super(AQL_QUERY_TYPE.BRAND_SAFETY_IAB, 'use_value', mergeFilters(filters));
  }
}

// for name filter type as value we are using English natural language presentations of the iab/apple keys (e.g. "Books and Literature" instead of "iab_books_and_literature")
export class AQLContextualFilterQuery extends AQLBaseQuery {
  constructor(
    contextualFilterType: AQLContextualFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq'>,
    value: IABFilterType | AppleFilterType | ContextualTaxonomy
  );
  constructor(
    contextualFilterType: AQLContextualFilterType,
    condition: Extract<keyof AQLQueryCondition, 'any_of'>,
    value: (IABFilterType | AppleFilterType)[] | ContextualTaxonomy[]
  );
  constructor(
    contextualFilterType: AQLContextualFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of'>,
    value:
      | IABFilterType
      | AppleFilterType
      | (IABFilterType | AppleFilterType)[]
      | ContextualTaxonomy
      | ContextualTaxonomy[]
  ) {
    super(contextualFilterType, condition, value);
  }
}

export class AQLContextualQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    filters: AQLContextualFilterQuery[]
  ) {
    super(AQL_QUERY_TYPE.CONTEXTUAL, condition, mergeFilters(filters));
  }
}

export class AQLContextualIABQuery extends AQLContextualQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    iabCategory: IABFilterType
  ) {
    super(condition, [
      new AQLContextualFilterQuery('taxonomy', 'eq', 'iab'),
      new AQLContextualFilterQuery('name', 'eq', iabCategory),
    ]);
  }
}

export class AQLNamedEntitiesFilterQuery extends AQLBaseQuery {
  constructor(
    namedEntityFilterType: AQLNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'wildcard'>,
    value: string
  );
  constructor(
    namedEntityFilterType: AQLNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'any_of'>,
    value: string[]
  );
  constructor(
    namedEntityFilterType: AQLNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of' | 'wildcard'>,
    value: string | string[]
  ) {
    super(namedEntityFilterType, condition, value);
  }
}

export class AQLNamedEntitiesQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    namedEntity: AQLNamedEntitiesFilterQuery[]
  ) {
    super(AQL_QUERY_TYPE.NAMED_ENTITIES, condition, mergeFilters(namedEntity));
  }
}

export class AQLLinkedNamedEntitiesFilterQuery extends AQLBaseQuery {
  constructor(
    linkedNamedEntityFilterType: AQLLinkedNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'wildcard'>,
    id: string
  );
  constructor(
    linkedNamedEntityFilterType: AQLLinkedNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'any_of'>,
    id: string[]
  );
  constructor(
    linkedNamedEntityFilterType: AQLLinkedNamedEntitiesFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of' | 'wildcard'>,
    id: string | string[]
  ) {
    super(linkedNamedEntityFilterType, condition, id);
  }
}

export class AQLLinkedNamedEntitiesQuery extends AQLBaseQuery {
  constructor(
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    linkedNamedEntities: AQLLinkedNamedEntitiesFilterQuery[]
  ) {
    super(AQL_QUERY_TYPE.LINKED_NAMED_ENTITIES, condition, mergeFilters(linkedNamedEntities));
  }
}

// composite queries

export class AQLCompositeQuery extends AQLBaseQuery {
  constructor(type: Extract<AQLQueryType, 'all' | 'any' | 'none'>, queries: AQLQuery[]) {
    super(type, 'use_value', queries);
  }
}

// this constant is here, to be in same file as class definitions, to avoid not initialized error
export const QUERY_CLASS_MAPPER = {
  [AQL_QUERY_TYPE.TITLE]: AQLTitleQuery,
  [AQL_QUERY_TYPE.DURATION]: AQLDurationQuery,
  [AQL_QUERY_TYPE.ORIGINAL_URL]: AQLOriginalURLQuery,
  [AQL_QUERY_TYPE.PUBLISHED_DATE]: AQLPublishedDateQuery,
  [AQL_QUERY_TYPE.CREATED_AT]: AQLCreatedAtQuery,
  [AQL_QUERY_TYPE.UPDATED_AT]: AQLUpdatedAtQuery,
  [AQL_QUERY_TYPE.GROUPS]: AQLGroupsQuery,
  [AQL_QUERY_TYPE.LANGUAGE]: AQLLanguageQuery,

  [AQL_QUERY_TYPE.TRANSCRIPT]: AQLTranscriptQuery,
  [AQL_QUERY_TYPE.BRAND_SAFETY_GARM]: AQLBrandSafetyGarmQuery,
  [AQL_QUERY_TYPE.BRAND_SAFETY_IAB]: AQLBrandSafetyIabQuery,
  [AQL_QUERY_TYPE.NAMED_ENTITIES]: AQLNamedEntitiesQuery,
  [AQL_QUERY_TYPE.LINKED_NAMED_ENTITIES]: AQLTypeQuery,
  [AQL_QUERY_TYPE.CONTEXTUAL]: AQLContextualQuery,

  [AQL_QUERY_TYPE.ID]: AQLIdQuery,
  [AQL_QUERY_TYPE.NAME]: AQLNameQuery,
  [AQL_QUERY_TYPE.TYPE]: AQLTypeQuery,
};
